'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { X } from '@phosphor-icons/react';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  fetchPRReviewExcludePatterns,
  storePRReviewExcludePatterns,
} from '@/apis/prReviewsConfig';
import { useToast } from '@/hooks/useToast';

interface Preset {
  id: string;
  label: string;
  description: string;
  patterns: string[];
}

const PRESETS: Preset[] = [
  {
    id: 'migrations',
    label: 'Migration files',
    description: '**/migrations/**, db/migrate/**, **/alembic/versions/**',
    patterns: ['**/migrations/**', 'db/migrate/**', '**/alembic/versions/**'],
  },
  {
    id: 'snapshots',
    label: 'Snapshot files',
    description: '**/*.snap',
    patterns: ['**/*.snap'],
  },
  {
    id: 'generated',
    label: 'Generated code',
    description: '**/*.generated.*, **/*.pb.go',
    patterns: ['**/*.generated.*', '**/*.pb.go'],
  },
  {
    id: 'fixtures',
    label: 'Test fixtures',
    description: '**/__fixtures__/**, **/testdata/**',
    patterns: ['**/__fixtures__/**', '**/testdata/**'],
  },
];

function presetIsActive(preset: Preset, patterns: string[]): boolean {
  return preset.patterns.every((p) => patterns.includes(p));
}

interface ExcludePatternsProps {
  orgUUID: string;
}

export default function ExcludePatterns({ orgUUID }: ExcludePatternsProps) {
  const showToast = useToast();
  const [patterns, setPatterns] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: fetchedPatterns } = useQuery({
    queryKey: ['prReviewExcludePatterns', orgUUID],
    queryFn: () => fetchPRReviewExcludePatterns(orgUUID),
    enabled: !!orgUUID,
  });

  useEffect(() => {
    if (fetchedPatterns) setPatterns(fetchedPatterns);
  }, [fetchedPatterns]);

  const saveMutation = useMutation({
    mutationFn: (next: string[]) => storePRReviewExcludePatterns(orgUUID, next),
  });

  const persist = (next: string[], previous: string[]) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      saveMutation.mutate(next, {
        onError: (error) => {
          console.error('storePRReviewExcludePatterns error:', error);
          setPatterns(previous);
          showToast({
            title: 'Could not save file exclusion patterns',
            status: 'error',
            duration: 4000,
            isClosable: true,
          });
        },
      });
    }, 400);
  };

  const togglePreset = (preset: Preset) => {
    const previous = [...patterns];
    const active = presetIsActive(preset, patterns);
    const next = active
      ? patterns.filter((p) => !preset.patterns.includes(p))
      : [...patterns, ...preset.patterns.filter((p) => !patterns.includes(p))];
    setPatterns(next);
    persist(next, previous);
  };

  const addCustomPattern = () => {
    const trimmed = inputValue.trim();
    if (!trimmed || patterns.includes(trimmed)) {
      setInputValue('');
      return;
    }
    const previous = [...patterns];
    const next = [...patterns, trimmed];
    setPatterns(next);
    persist(next, previous);
    setInputValue('');
  };

  const removePattern = (pattern: string) => {
    const previous = [...patterns];
    const next = patterns.filter((p) => p !== pattern);
    setPatterns(next);
    persist(next, previous);
  };

  const customPatterns = patterns.filter(
    (p) => !PRESETS.flatMap((pr) => pr.patterns).includes(p)
  );

  return (
    <div className="mt-8 motion-stagger">
      <div className="py-4 flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold text-ds-text-darker mb-1">
            File Exclusion
          </h3>
          <p className="text-sm text-ds-text-subtle">
            Files matching these patterns will be skipped during code review.
          </p>
        </div>
        {saveMutation.isPending && (
          <span className="text-xs text-ds-text-subtler mt-1">Saving…</span>
        )}
      </div>

      {/* Preset cards */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {PRESETS.map((preset) => {
          const active = presetIsActive(preset, patterns);
          return (
            <div
              key={preset.id}
              className="flex items-start justify-between gap-3 rounded-lg border border-ds-border-subtlest bg-ds-surface-white p-4"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-ds-text-darker leading-tight">
                  {preset.label}
                </p>
                <p className="text-xs text-ds-text-subtler mt-0.5 break-all leading-snug">
                  {preset.description}
                </p>
              </div>
              <Switch
                checked={active}
                onCheckedChange={() => togglePreset(preset)}
                className="shrink-0 mt-0.5"
              />
            </div>
          );
        })}
      </div>

      {/* Custom patterns */}
      <div className="rounded-lg border border-ds-border-subtlest bg-ds-surface-white p-4">
        <p className="text-sm font-medium text-ds-text-darker mb-3">
          Custom patterns
        </p>

        {customPatterns.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {customPatterns.map((pattern) => (
              <span
                key={pattern}
                className="inline-flex items-center gap-1 rounded-md bg-ds-brand-lightest border border-ds-border-subtlest px-2.5 py-1 text-xs font-medium text-ds-text-darker"
              >
                {pattern}
                <button
                  onClick={() => removePattern(pattern)}
                  className="ml-0.5 text-ds-text-subtler hover:text-ds-text-darker transition-colors"
                  aria-label={`Remove ${pattern}`}
                >
                  <X size={12} weight="bold" />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addCustomPattern();
              }
            }}
            placeholder="e.g. **/migrations/** or **/*.sql"
            className="h-8 text-sm"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={addCustomPattern}
            disabled={!inputValue.trim()}
            className="shrink-0"
          >
            Add
          </Button>
        </div>
      </div>
    </div>
  );
}
